import React, { useCallback, useState } from 'react';
import {
  BaseActionSheet,
  Props as BaseActionSheetProps,
} from './base-action-sheet';
import { HeaderBackButton } from './header-back-button';
import { Position } from '@/contexts/toast-context';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { setNextLayoutAnimation } from '@/utils/layout-animation';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { SvgProps } from 'react-native-svg';
import { TOAST_MARGIN } from './toast';
import { useLayoutContext } from '@/contexts/layout-context';

type RouteKey = string;

export interface Route {
  contentStyle?: StyleProp<ViewStyle>;
  dismissKey?: RouteKey;
  modalHeaderStyle?: StyleProp<ViewStyle>;
  modalStyle?: StyleProp<ViewStyle>;
  previousKey: RouteKey | null;
  renderAsPopover?: boolean;
  showCloseButton?: boolean;
  showSmallLayoutBackButton?: boolean;
  showSmallLayoutTitle?: boolean;
  smallLayoutBackButtonIcon?: React.FC<SvgProps>;
  smallLayoutBackButtonIconProps?: SvgProps;
  smallLayoutBackButtonStyle?: StyleProp<ViewStyle>;
  subtitle?: string;
  title?: string;
}

export type JumpToFn<K> = (routeKey: K) => void;

export interface ScreenRendererProps<T, K> {
  close: () => void;
  jumpTo: JumpToFn<K>;
  routeKey: keyof T;
}

type PopoverProps = Pick<
  BaseActionSheetProps,
  | 'popoverStyle'
  | 'popoverTarget'
  | 'onSwipeCancel'
  | 'onSwipeMove'
  | 'propagateSwipe'
  | 'swipeDirection'
>;

interface Props<T, K> extends PopoverProps {
  animateTransitions?: boolean;
  avoidKeyboard?: boolean;
  contentStyle?: StyleProp<ViewStyle>;
  hasStickyHeader?: boolean;
  initialRoute: K;
  isFocusableForKeyboardShortcuts?: boolean;
  modalHeaderStyle?: StyleProp<ViewStyle>;
  modalStyle?: StyleProp<ViewStyle>;
  onClose: (routeKey: K) => void;
  onRouteChange?: (nextKey: K, previousKey: K) => void;
  renderAsPopover?: boolean;
  renderScene: (props: ScreenRendererProps<T, K>) => React.ReactNode;
  routes: T;
  showToastsExternally?: boolean;
  subtitleStyle?: StyleProp<TextStyle>;
  titleStyle?: StyleProp<TextStyle>;
}

export const CombinedActionSheet = <T extends Record<RouteKey, Route>>({
  animateTransitions,
  avoidKeyboard = true,
  hasStickyHeader = true,
  modalHeaderStyle,
  modalStyle,
  contentStyle,
  initialRoute,
  isFocusableForKeyboardShortcuts,
  onClose,
  onRouteChange,
  onSwipeCancel,
  onSwipeMove,
  popoverStyle,
  popoverTarget,
  propagateSwipe,
  renderAsPopover,
  renderScene,
  routes,
  showToastsExternally,
  swipeDirection,
  subtitleStyle,
  titleStyle,
}: Props<T, keyof T>) => {
  const { isSmallLayout } = useLayoutContext();

  const [isOpen, setIsOpen] = useState(true);
  const [activeRoute, setActiveRoute] = useState<keyof T>(initialRoute);
  const prevRoute = routes[activeRoute].previousKey;

  const jumpTo = useCallback(
    (routeKey: keyof T) => {
      if (animateTransitions) {
        setNextLayoutAnimation();
      }
      onRouteChange?.(routeKey, activeRoute);
      setActiveRoute(routeKey);
    },
    [activeRoute, animateTransitions, onRouteChange],
  );

  const {
    contentStyle: routeContentStyle,
    dismissKey,
    modalHeaderStyle: routeModalHeaderStyle,
    modalStyle: routeModalStyle,
    showCloseButton,
    showSmallLayoutBackButton,
    showSmallLayoutTitle,
    smallLayoutBackButtonIcon,
    smallLayoutBackButtonIconProps,
    smallLayoutBackButtonStyle,
    subtitle,
    title,
  } = routes[activeRoute];

  // Setting closing to true will cause action sheet to close (via isVisible prop), which will trigger onModalHide
  // (so rendered scenes calling close() will go through a layer of indirection as modal actually closes)
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleDismiss = useCallback(() => {
    if (dismissKey) {
      jumpTo(dismissKey);
      return;
    }
    handleClose();
  }, [dismissKey, handleClose, jumpTo]);

  const popoverProps: PopoverProps | null = renderAsPopover
    ? {
        popoverStyle,
        popoverTarget,
      }
    : null;

  return (
    <BaseActionSheet
      {...popoverProps}
      avoidKeyboard={avoidKeyboard}
      contentStyle={[contentStyle, routeContentStyle]}
      hasStickyHeader={hasStickyHeader}
      isFocusableForKeyboardShortcuts={isFocusableForKeyboardShortcuts}
      isVisible={isOpen}
      modalHeaderStyle={[modalHeaderStyle, routeModalHeaderStyle]}
      modalStyle={[modalStyle, routeModalStyle]}
      onClose={handleDismiss}
      onModalBackArrowPress={prevRoute ? () => jumpTo(prevRoute) : undefined}
      onModalHide={() => onClose(activeRoute)}
      onSwipeCancel={onSwipeCancel}
      onSwipeComplete={handleClose}
      onSwipeMove={onSwipeMove}
      propagateSwipe={propagateSwipe}
      showCloseButton={showCloseButton}
      showToastsExternally={showToastsExternally}
      swipeDirection={swipeDirection}
      title={title}
      toastContainerStyle={
        isSmallLayout ? styles.toastContainer : styles.toastContainerLarge
      }
      toastPosition={Position.TOP}
    >
      {isSmallLayout && showSmallLayoutBackButton ? (
        <HeaderBackButton
          containerStyle={[styles.backButton, smallLayoutBackButtonStyle]}
          Icon={smallLayoutBackButtonIcon}
          iconProps={{
            testID: prevRoute
              ? 'action-sheet-back-button'
              : 'action-sheet-close-button',
            ...smallLayoutBackButtonIconProps,
          }}
          onPress={prevRoute ? () => jumpTo(prevRoute) : handleClose}
        />
      ) : null}
      {isSmallLayout && showSmallLayoutTitle && title ? (
        <Text style={[TYPOGRAPHY.h2, styles.title, titleStyle]}>{title}</Text>
      ) : null}
      {isSmallLayout && showSmallLayoutTitle && subtitle ? (
        <Text style={[TYPOGRAPHY.h4, styles.subtitle, subtitleStyle]}>
          {subtitle}
        </Text>
      ) : null}
      {renderScene({
        close: handleClose,
        jumpTo,
        routeKey: activeRoute,
      })}
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  backButton: {
    // Override platform-specific margin
    marginLeft: SCREEN_PADDING,
    paddingTop: 0,
  },
  subtitle: {
    marginBottom: 16,
    marginTop: -8,
  },
  title: {
    marginBottom: 16,
  },
  toastContainer: {
    // Show toasts at the top of the container with standard screen padding
    top: SCREEN_PADDING - TOAST_MARGIN,
  },
  toastContainerLarge: {
    // Remove toast top margin so it sits flush with the top of the container
    top: -TOAST_MARGIN,
  },
});
